import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import {
  Alert,
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { RegionLoading } from '~/components/common/RegionLoading'
import { BusinessAreasCheckboxes } from '~/pages/Settings/components/BusinessAreasCheckboxes'
import { TickerSwitch } from '~/pages/Settings/components/TickerSwitch'
import { usePersonalSettings } from '~/pages/Settings/hooks/usePersonalSettings'

// design `.st-grid-col` 相当: 列内のカードを 16px (Spacing.4) 等間隔で縦積みする。
// design/settings.html line 46 参照。
const settingColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px', // Spacing.4
  minWidth: 0,
}

// design/settings.html の `?focus=<id-suffix>` 処理 (#st-<suffix>-card へ scroll + 一時 outline) 相当
// (#9924 / AC-D-001)。業務画面のリンクから目的の設定カードへ誘導する受け側。
// 対象は design が id を定義したカードのうち React にあるもの (業務エリア) だけにする。
// design に無い key を足すと URL の契約が黙って広がるため。
const FOCUS_HIGHLIGHT_MS = 1800
const FOCUS_SCROLL_DELAY_MS = 50

const SETTING_FOCUS_KEYS = ['business-area'] as const
type SettingFocusKey = (typeof SETTING_FOCUS_KEYS)[number]

/** `?focus=` の値を対象 key に解決する。対象外の値は null (= 何もしない)。 */
const toSettingFocusKey = (raw: string | null): SettingFocusKey | null =>
  SETTING_FOCUS_KEYS.find((key) => key === raw) ?? null

const settingCardDomId = (key: SettingFocusKey): string => `st-${key}-card`

type SettingCardProps = {
  /** `?focus=` で辿り着けるカードの key。DOM id は `st-<focusKey>-card`。対象外のカードは省略する。 */
  focusKey?: SettingFocusKey
  highlighted?: boolean
  label: string
  hint?: string
  children: React.ReactNode
}

const SettingCard = ({
  focusKey,
  highlighted = false,
  label,
  hint,
  children,
}: SettingCardProps): React.ReactElement => {
  return (
    <div
      // ?focus= で一時強調している間だけ付く (テストの観測点。見た目は outline)
      data-highlighted={highlighted || undefined}
      id={focusKey !== undefined ? settingCardDomId(focusKey) : undefined}
      style={{
        // design `#st-business-area-card` と同じ (固定ヘッダーの下に隠れないよう余白を取る)
        scrollMarginTop: focusKey !== undefined ? '80px' : undefined,
        outlineStyle: highlighted ? 'solid' : 'none',
        outlineWidth: '2px',
        // design は `var(--focus-clarity)` (DS のフォーカスリングの色)
        outlineColor: LightModeSemanticColors.Focus.clarity.value,
        outlineOffset: '2px',
        transition: 'outline 0.3s ease-out',
        background: LightModeSemanticColors.Surface.primary.value,
        border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (BorderRadius.radiiLg)
        borderRadius: '8px',
        padding: '16px', // Spacing.4
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.0200)', // ShadowsToken.shadSm
        display: 'flex',
        flexDirection: 'column',
        gap: '8px', // Spacing.2
      }}
    >
      {/* design `.st-card-label` 相当: font-size lg (16px) + weight 700。
          design/css/components.css line 1597-1602 参照。 */}
      <div
        style={{
          fontWeight: 700,
          fontSize: fontSizes.lg.value,
          color: LightModeSemanticColors.Text.highEmphasis.value,
        }}
      >
        {label}
      </div>
      {hint !== undefined && (
        // design `.st-hint` 相当: font-size md (14px)。
        // design/css/components.css line 1603-1607 参照。
        <div
          style={{
            fontSize: fontSizes.md.value,
            color: LightModeSemanticColors.Text.midEmphasis.value,
          }}
        >
          {hint}
        </div>
      )}
      {/* design `.ecl-radio` / `.ecl-checkbox` / `.gc-stack-label` 相当: font-size md (14px) を継承させる
          (= 個別 component 改修ではなく、 card content 全体で 14px を default にする)。 */}
      <div style={{ marginTop: '8px', fontSize: fontSizes.md.value }}>
        {children}
      </div>
    </div>
  )
}

export const PersonalSettingsForm = (): React.ReactElement => {
  const { t } = useTranslation()
  const {
    settings,
    isLoading,
    updateShowTicker,
    updateBusinessAreas,
    selectAllBusinessAreas,
    saveError,
  } = usePersonalSettings()

  const [searchParams] = useSearchParams()
  const focusKey = toSettingFocusKey(searchParams.get('focus'))
  const [highlightedKey, setHighlightedKey] = useState<SettingFocusKey | null>(
    null,
  )

  // カードは読み込み完了後に描画されるため isLoading が外れてから scroll・強調を始める。
  // 対象外の値は toSettingFocusKey が null にするので何もしない。設定値は変更しない。
  useEffect(() => {
    if (isLoading || focusKey === null) return
    const timer = setTimeout(() => {
      document
        .getElementById(settingCardDomId(focusKey))
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      setHighlightedKey(focusKey)
    }, FOCUS_SCROLL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isLoading, focusKey])

  // 強調は始まった経路によらず一定時間で解除する (開始側の effect と分けているので、
  // 開始側の cleanup で解除タイマーが消えない)。
  useEffect(() => {
    if (highlightedKey === null) return
    const timer = setTimeout(() => setHighlightedKey(null), FOCUS_HIGHLIGHT_MS)
    return () => clearTimeout(timer)
  }, [highlightedKey])

  // 描くのは URL の focus が今も同じカードを指している間だけ。強調中に focus の無い URL
  // (または別の値) へ移った時点で outline を消す (解除タイマーの満了を待たない)。
  const visibleHighlightKey =
    highlightedKey === focusKey ? highlightedKey : null

  if (isLoading) {
    return <RegionLoading minHeight="200px" isLoading />
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} // Spacing.4
    >
      {saveError !== null && <Alert status="error" title={t(saveError)} />}

      <div style={settingColumnStyle}>
        {/* テーマカードは **P2 の実装着手まで画面に出さない**
              (spec `settings/personal-settings.md` §1「画面表示: P2 実装着手まで非表示」。
              issue #6156、 2026-08-24 PdM 判断)。
              選んでも画面に反映されない状態 (`index.tsx` の `forcedTheme="light"` で
              ライト固定、 保存値 `ebalance-theme` を読むコードが無い) のため、
              `release-scope.md` の既定ルール「priority 降格だけでは UI を消さない」の
              例外として非表示にした。
              TODO(#6156): P2 で戻す時は、 ここの `SettingCard` を復活させ、
              `index.tsx` の `forcedTheme="light"` の撤去と対で行う。
              部品はそのまま残してある: `ThemeRadio` component / `usePersonalSettings` の
              `updateTheme` / i18n `settings.personal.theme.*`。 この file の import と
              destructuring だけを外している (未使用で lint に落ちるため)。 */}
        <SettingCard
          hint={t('page:settings.personal.ticker.hint')}
          label={t('page:settings.personal.ticker.label')}
        >
          <TickerSwitch
            onChange={updateShowTicker}
            value={settings?.showTicker ?? true}
          />
        </SettingCard>

        <SettingCard
          focusKey="business-area"
          highlighted={visibleHighlightKey === 'business-area'}
          hint={t('page:settings.personal.businessAreas.hint')}
          label={t('page:settings.personal.businessAreas.label')}
        >
          <BusinessAreasCheckboxes
            onChange={updateBusinessAreas}
            onSelectAll={selectAllBusinessAreas}
            value={settings?.businessAreas ?? []}
          />
        </SettingCard>
      </div>
    </div>
  )
}
